import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import Isvg from 'react-inlinesvg';
import {
  fromUnixTime,
  differenceInMinutes,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  isToday,
  isYesterday,
  isValid,
} from 'date-fns';

import { reviewPropTypes } from 'constants/propTypes';
import Button from 'components/Button';
import { PFE_ICONS } from 'constants/icons';
import { Dropdown, Menu } from 'components/Dropdown';
import Message from 'components/Message';
import ReplyToReviewForm from '../ReplyToReviewForm/ReplyToReviewFormContainer';

import './RatingsListItem.scss';
import { RatingsStarsReview } from './RatingsStarsReview';
import { RatingsReviewer } from './RatingsReviewer';

const messages = defineMessages({
  report: {
    id: 'ratings.report',
    defaultMessage: 'Report',
  },
  postReplyLink: {
    id: 'ratings.post-reply-link',
    defaultMessage: 'Reply',
  },
  deleteReplyLink: {
    id: 'ratings.delete-reply-link',
    defaultMessage: 'Delete this reply',
  },
  justNow: {
    id: 'ratings.just-now',
    defaultMessage: 'just now',
  },
  today: {
    id: 'ratings.today',
    defaultMessage: 'today',
  },
  yesterday: {
    id: 'ratings.yesterday',
    defaultMessage: 'yesterday',
  },
  numberOfDays: {
    id: 'ratings.number-of-days',
    defaultMessage: '{days, plural, one {# day} other {# days}} ago',
  },
  numberOfWeeks: {
    id: 'ratings.number-of-weeks',
    defaultMessage: '{weeks, plural, one {# week} other {# weeks}} ago',
  },
  numberOfMonths: {
    id: 'ratings.number-of-months',
    defaultMessage: '{months, plural, one {# month} other {# months}} ago',
  },
});

const RATINGS_DROPDOWN_ITEM = [
  {
    key: 'POST_REPLY',
    render: () => <Message message={messages.postReplyLink} />,
    action: ({ handlePostReplyClick }) => handlePostReplyClick(),
    shouldRender: ({ isMobile, showReplyForm }) => isMobile && !showReplyForm,
  },
  {
    key: 'REPORT_RATING',
    render: () => <Message className="ratings-list-item__report" message={messages.report} />,
    action: ({ reportReviewAsAbuse, review: { id } }) => reportReviewAsAbuse(id),
    shouldRender: () => true,
  },
];

const RatingsListItem = ({
  review: {
    id,
    className,
    review,
    teacherFullName,
    rating,
    studioResponse,
    createdOn,
    isPublic,
    name,
    profilePictureUrl,
  },
  deleteStudioReply,
  reportReviewAsAbuse,
  isMobile,
  isVenueFoodAndBeverage,
}) => {
  const [showReplyForm, setShowReplyForm] = useState(!!studioResponse);
  const intl = useIntl();

  const handlePostReplyClick = () => setShowReplyForm(true);

  const handleCancelReplyClick = () => setShowReplyForm(false);

  const handleRatingDropdownSelect = ({ action }) =>
    action({ reportReviewAsAbuse, handlePostReplyClick, review: { id }, showReplyForm });

  const ratingDropdownItems = RATINGS_DROPDOWN_ITEM.filter(item => item.shouldRender({ isMobile, showReplyForm }));

  const teacherFullNameCopy = teacherFullName ? ` - ${teacherFullName}` : '';
  const ratingsListItemTitle = isVenueFoodAndBeverage ? className : `${className}${teacherFullNameCopy}`;

  const renderOptions = () => (
    <Dropdown
      attachment="right"
      className="ratings-list-item__dropdown"
      items={ratingDropdownItems}
      getKey={item => item.key}
      getText={item => item.key}
      renderMenuItem={(key, isSelected, item) => item.render()}
      onSelect={handleRatingDropdownSelect}
      Toggle={({ toggleMenu }) => (
        <button
          type="button"
          className="ratings-list-item__toggle-button ratings-list-item__toggle-svg"
          onClick={toggleMenu}>
          <Isvg src={PFE_ICONS.threeDots} />
        </button>
      )}
      Content={Menu}
    />
  );

  const getTimePassed = timestamp => {
    const today = new Date();

    const { formatMessage, formatDate } = intl;
    const date = fromUnixTime(timestamp);

    if (!timestamp || !isValid(date)) return null;

    if (differenceInMinutes(today, date) <= 3) return formatMessage(messages.justNow);

    if (isToday(date)) return formatMessage(messages.today);

    if (isYesterday(date)) return formatMessage(messages.yesterday);

    const inDays = differenceInDays(today, date);
    if (inDays < 7) return formatMessage(messages.numberOfDays, { days: inDays });

    const inWeeks = differenceInWeeks(today, date);
    if (inWeeks < 5) return formatMessage(messages.numberOfWeeks, { weeks: inWeeks });

    const inMonths = differenceInMonths(today, date);
    if (inMonths < 12) return formatMessage(messages.numberOfMonths, { months: inMonths });

    return formatDate(date);
  };

  const numericRating = Number(rating);
  const timePassed = getTimePassed(createdOn);

  const renderReview = () => (
    <div className="ratings-list-item__cells">
      {isPublic ? (
        <RatingsReviewer
          name={name}
          profilePictureUrl={profilePictureUrl}
          rating={numericRating}
          timePassed={timePassed}
        />
      ) : (
        <div className="grid__item 9/10 md-3/4 lg-3/4">
          <RatingsStarsReview rating={numericRating} timePassed={timePassed} />
        </div>
      )}

      <div className="grid__item 1/10 md-1/4 lg-1/4 ratings-list-item__cell-actions">
        {!isMobile && !showReplyForm && (
          <Message
            as={Button}
            className="ratings-list-item__reply-button"
            styled="primary-outline"
            onClick={handlePostReplyClick}
            message={messages.postReplyLink}
          />
        )}
        {renderOptions()}
      </div>
      <div className="grid__item md-3/4 lg-3/4 ratings-list-item__cell-review">
        <p className="ratings-list-item__title">{ratingsListItemTitle}</p>
        <div className="ratings-list-item__review">{review}</div>
      </div>
    </div>
  );

  const renderStudioReply = () => (
    <div className="ratings-list-item__column">
      <p className="ratings-list-item__studio-reply">{studioResponse}</p>
      <Message
        as="a"
        className="ratings-list-item__delete-reply-link"
        onClick={() => deleteStudioReply(id)}
        message={messages.deleteReplyLink}
      />
    </div>
  );

  const renderReplyForm = () => (
    <>
      <div className="grid__item lg-1/5 sm-1/3 ratings-list-item__column" />
      <div className="grid__item lg-3/5 sm-3/4 xs-5/5 ratings-list-item__column">
        {showReplyForm && <ReplyToReviewForm reviewId={id} onCancelClick={handleCancelReplyClick} />}
      </div>
    </>
  );

  return (
    <div className="ratings-list-item grid grid--flush" data-cypress="ratings-list-item">
      {renderReview()}
      {studioResponse ? renderStudioReply() : renderReplyForm()}
    </div>
  );
};

RatingsListItem.propTypes = {
  studioResponse: PropTypes.string,
  review: PropTypes.shape(reviewPropTypes),
  reportReviewAsAbuse: PropTypes.func,
  deleteStudioReply: PropTypes.func,
  isMobile: PropTypes.bool,
  isVenueFoodAndBeverage: PropTypes.bool,
};

export default RatingsListItem;
